import React, { CSSProperties, FC, useMemo } from 'react'
import styled from 'styled-components'

import { Icon, Button, ScrollHWrapper } from 'components/base-ui'
import ProductCardV2 from 'components/ProductCardV2'
import { ProductType } from 'types/product'
import { LANGUAGES } from 'helpers/language'
import { useTranslation } from 'react-i18next'
import { MembershipProgramPublicType, Vendor } from 'types/delivery'
import { VendorType } from 'services/hooks/dine-in/useGetVendorDetail'

interface Props {
  label?: string
  iconName?: string
  layout?: 'normal' | 'slide'
  addToCart: boolean
  addToCartIconName?: string
  onClickProduct: (product: ProductType) => void
  onAddToCart: (product: ProductType) => void
  viewMore?: boolean
  viewMoreSlide?: string
  onViewMore?: () => void
  data: ProductType[]
  vendor: Vendor | VendorType
  lang: LANGUAGES
  lazyLoadScrollContainerID?: string
  overflow?: boolean
  membershipProgram: MembershipProgramPublicType | null
  isOutline?: boolean
  addToCartIconClass?: string
  style?: CSSProperties
}

const ProductList: FC<Props> = ({
  label = '',
  iconName = '',
  layout = 'normal',
  addToCart = false,
  addToCartIconName = 'plus-light',
  onClickProduct,
  onAddToCart = (product) => {},
  viewMore = false,
  viewMoreSlide = false,
  onViewMore = () => {},
  data,
  vendor,
  lang = LANGUAGES.TH,
  lazyLoadScrollContainerID = '',
  overflow = true,
  membershipProgram,
  isOutline = false,
  addToCartIconClass = '',
  ...rest
}) => {
  const { t } = useTranslation()

  const productListContent = useMemo(() => {
    if (data) {
      if (layout === 'normal') {
        return (
          <ProductCards>
            {data.map((val, i) => (
              <ProductCardV2
                overflow={overflow}
                vendor={vendor}
                data={val}
                key={i}
                addToCart={addToCart}
                onAddToCart={() => onAddToCart(val)}
                onClickProduct={onClickProduct}
                addToCartIconName={addToCartIconName}
                lazyLoadScrollContainerID={lazyLoadScrollContainerID}
                membershipProgram={membershipProgram}
                isOutline={isOutline}
                addToCartIconClass={addToCartIconClass}
              />
            ))}
          </ProductCards>
        )
      } else if (layout === 'slide') {
        return (
          <SlideWrapper>
            <ScrollHWrapper>
              {data.map((val, i) => (
                <ProductCardV2
                  overflow={overflow}
                  vendor={vendor}
                  data={val}
                  key={i}
                  mode="slide"
                  addToCart={addToCart}
                  onAddToCart={() => onAddToCart(val)}
                  onClickProduct={onClickProduct}
                  addToCartIconName={addToCartIconName}
                  lazyLoadScrollContainerID={lazyLoadScrollContainerID}
                  membershipProgram={membershipProgram}
                  addToCartIconClass={addToCartIconClass}
                />
              ))}
              {viewMoreSlide && <CardSlideViewMore onClick={onViewMore}>{t('btnViewAllSlide')}</CardSlideViewMore>}
            </ScrollHWrapper>
          </SlideWrapper>
        )
      }
    } else {
      return null
    }
  }, [layout, data, lang, vendor, addToCart, onAddToCart, lazyLoadScrollContainerID])

  return (
    <Section {...rest}>
      {label && (
        <SectionTitle className="category-name">
          {iconName && <Icon name={iconName} />}
          {label}
        </SectionTitle>
      )}

      {productListContent}

      {viewMore && (
        <SectionFooter>
          <Button md2 outline onClick={onViewMore}>
            {t('btnViewAll')}
          </Button>
        </SectionFooter>
      )}
    </Section>
  )
}

export default ProductList

const Section = styled.div`
  margin-top: ${({ theme }) => theme.spacing.S};
  padding-bottom: ${({ theme }) => theme.spacing['3XS']};
`

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.gray700};

  i {
    margin-right: ${({ theme }) => theme.spacing.XS};
    font-size: ${({ theme }) => theme.fontSize.xl};
    color: ${({ theme }) => theme.colors.primary};
  }
`

const ProductCards = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: -${({ theme }) => theme.spacing.S};
  margin-right: -${({ theme }) => theme.spacing.S};
  margin-bottom: ${({ theme }) => theme.spacing.S};
`

const SlideWrapper = styled.div`
  width: calc(100% + 40px) !important;
  margin-left: -${({ theme }) => theme.spacing['3XL']};
  margin-bottom: -${({ theme }) => theme.spacing['3XL']};
  padding-bottom: ${({ theme }) => theme.spacing.L};
  white-space: nowrap;
`

const SectionFooter = styled.div`
  margin-top: ${({ theme }) => theme.spacing.XL};
  text-align: center;
`

const CardSlideViewMore = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.L};
  margin-right: ${({ theme }) => theme.spacing['3XL']};
  margin-bottom: 0;
  width: 135px;
  height: 180px;
  border-radius: 10px;
  padding-right: 0;
  padding-left: 0;
  vertical-align: top;
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.second};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.fontWeight.semiBold};
  font-size: ${({ theme }) => theme.fontSize.xl};
  white-space: pre-wrap;
  transition: 0.2s;

  &:first-child {
    margin-left: ${({ theme }) => theme.spacing['3XL']};
  }

  .touch &.touch,
  &:active {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.element.white};
  }
`
